import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, GraduationCap, Users, Home, Building2, Baby, Heart, Shield, BookOpen, FileText, Target, CheckCircle, AlertTriangle } from 'lucide-react';

const theme = {
  primary: '#7C3AED',
  secondary: '#EC4899',
  background: '#FAF5FF',
  text: '#1E293B',
};

const slides = [
  {
    id: 1,
    title: "Présentation Générale du Bac Pro ASSP",
    subtitle: "Baccalauréat Professionnel Accompagnement, Soins et Services à la Personne",
    description: "Le baccalauréat professionnel Accompagnement, Soins et Services à la Personne (ASSP) forme des professionnels capables d'accompagner des personnes de tous âges dans les actes de la vie quotidienne, les soins d'hygiène et de confort, et le maintien de la vie sociale, à domicile ou en structure. Il prépare à une insertion professionnelle directe, mais aussi à la poursuite d'études dans les secteurs sanitaire et social.",
    type: "title",
    icon: "GraduationCap",
  },
  {
    id: 2,
    title: "Finalités du diplôme",
    type: "content",
    content: [
      "Le Bac Pro ASSP forme des professionnels de l'accompagnement et du soin",
      "Diplôme de niveau 4 inscrit au RNCP",
      "Accessible par la formation initiale, continue ou la VAE",
      "Permet d'exercer auprès de publics variés en perte d'autonomie",
      "Ouvre des perspectives de poursuite d'études (BTS, IFSI, IFAS...)"
    ]
  },
  {
    id: 3,
    title: "Secteurs d'exercice",
    type: "grid",
    items: [
      { title: "A domicile", desc: "SAAD, SSIAD, SPASAD, emploi direct", icon: "Home" },
      { title: "En structure", desc: "EHPAD, hôpital, clinique, SSR", icon: "Building2" },
      { title: "En milieu scolaire", desc: "Écoles maternelles, AESH, périscolaire", icon: "BookOpen" },
      { title: "Handicap", desc: "MAS, FAM, IME, ESAT, foyer de vie", icon: "Heart" },
    ]
  },
  {
    id: 4,
    title: "Publics accompagnés",
    type: "list",
    footer: "Pour chaque public, l'accompagnement doit être adapté à l'âge, aux capacités, à l'histoire de vie, aux besoins et aux projets.",
    items: [
      { title: "Enfants", desc: "De la naissance à l'adolescence – crèche, école, domicile" },
      { title: "Adolescents", desc: "Accompagnement éducatif et social, soutien scolaire" },
      { title: "Adultes", desc: "En situation de handicap, maladie chronique, convalescence" },
      { title: "Personnes âgées", desc: "Autonomes, dépendantes, en EHPAD ou à domicile" },
      { title: "Personnes en situation de handicap", desc: "Handicap moteur, mental, psychique, sensoriel, polyhandicap" },
    ]
  },
  {
    id: 5,
    title: "Rénovation 2022 : ce qui change",
    type: "renovation",
    blocs: [
      { num: "Bloc 1", title: "Accompagner la personne dans une approche globale et individualisée", desc: "Relation, communication, projet de vie, activités d'autonomie et de vie sociale.", color: "#8B5CF6" },
      { num: "Bloc 2", title: "Réaliser des soins d'hygiène, de confort, de sécurité et des activités de la vie quotidienne", desc: "Toilette, confort, alimentation, hygiène de l'environnement, prévention des risques.", color: "#06B6D4" },
      { num: "Bloc 3", title: "Travailler en équipe pluriprofessionnelle et en réseau, communiquer dans un contexte professionnel", desc: "Transmissions, dossier de la personne, outils numériques, qualité, gestion des risques.", color: "#F59E0B" },
      { num: "Bloc 4", title: "Mettre en œuvre des actions d'éducation à la santé", desc: "Diagnostic éducatif, projet, animation et évaluation d'actions de prévention.", color: "#10B981" },
    ],
    content: [
      "Ces blocs s'appuient sur des pôles professionnels (accompagnement de la vie quotidienne, soins et hygiène, vie sociale, travail en équipe) qui structurent les situations de travail rencontrées",
      "Suppression des options « à domicile » et « en structure »",
      "Tronc commun unique pour tous les candidats",
      "Renforcement de la démarche de projet individualisé",
      "Plus grande place à l'éducation à la santé (Bloc 4)"
    ]
  },
  {
    id: 6,
    title: "Les 4 blocs de compétences",
    type: "blocks",
    items: [
      { num: "BC1", title: "Accompagner la personne dans une approche globale et individualisée", color: "#8B5CF6" },
      { num: "BC2", title: "Soins d'hygiène, de confort et de sécurité", color: "#06B6D4" },
      { num: "BC3", title: "Travailler et communiquer en équipe pluriprofessionnelle", color: "#F59E0B" },
      { num: "BC4", title: "Réaliser des actions d'éducation à la santé", color: "#10B981" },
    ]
  },
  {
    id: 7,
    title: "Posture professionnelle",
    type: "content",
    content: [
      "Respect de la dignité et de l'intimité de la personne",
      "Bienveillance et empathie dans la relation d'aide",
      "Discrétion professionnelle et secret professionnel",
      "Respect du droit des usagers (loi 2002-2)",
      "Capacité d'adaptation aux situations et aux publics",
      "Travail en équipe et communication professionnelle"
    ]
  },
  {
    id: 8,
    title: "Éthique et secret professionnel",
    type: "content",
    highlight: true,
    content: [
      "Secret professionnel : obligation légale (art. 226-13 du Code pénal)",
      "Secret partagé : partage d'informations strictement nécessaires au sein de l'équipe",
      "Principe de non-jugement envers les personnes accompagnées",
      "Respect des choix, des croyances et des habitudes de vie",
      "Consentement éclairé : informer et respecter les décisions",
      "Distance professionnelle : ni trop proche, ni trop distant"
    ]
  },
  {
    id: 9,
    title: "Place du Bac ASSP dans l'équipe",
    type: "list",
    items: [
      { title: "Sous la responsabilité de l'IDE", desc: "Collaboration avec l'infirmier(e) pour les soins d'hygiène et de confort" },
      { title: "En lien avec l'AS/AES", desc: "Complémentarité des rôles dans l'accompagnement quotidien" },
      { title: "Transmission à l'équipe", desc: "Observations, transmissions ciblées, participation aux réunions" },
      { title: "Limites de compétences", desc: "Connaissance précise de son champ d'intervention – ne pas dépasser son rôle" },
    ]
  },
  {
    id: 10,
    title: "En résumé",
    type: "summary",
    items: [
      "Le Bac Pro ASSP est un diplôme polyvalent d'accompagnement et de soins",
      "4 blocs de compétences couvrant tous les aspects du métier",
      "Rénovation 2022 : tronc commun, PFMP diversifiées, certification CCF",
      "Posture professionnelle : éthique, secret, bienveillance, limites",
      "Débouchés variés : domicile, structure, scolaire, handicap"
    ]
  },
];

const SlideRenderer = ({ slide }) => {
  const renderContent = () => {
    switch (slide.type) {
      case 'title':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <GraduationCap size={64} color={theme.primary} style={{ margin: '0 auto 1.5rem' }} />
            <h1 style={{ fontSize: '2rem', color: theme.primary, marginBottom: '1rem' }}>{slide.title}</h1>
            <p style={{ fontSize: '1.1rem', color: '#64748B', marginBottom: slide.description ? '1.5rem' : 0 }}>{slide.subtitle}</p>
            {slide.description && (
              <p style={{ fontSize: '0.95rem', color: theme.text, lineHeight: '1.7', textAlign: 'left', maxWidth: '700px', margin: '0 auto', background: '#F5F3FF', padding: '1.25rem 1.5rem', borderRadius: '12px', borderLeft: `4px solid ${theme.primary}` }}>{slide.description}</p>
            )}
          </div>
        );
      case 'content':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {slide.content.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', padding: '0.8rem', background: slide.highlight ? '#F5F3FF' : '#F8FAFC', borderRadius: '8px', borderLeft: `3px solid ${theme.primary}` }}>
                  <CheckCircle size={18} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'renovation':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '0.9rem', color: '#64748B', marginBottom: '1.25rem' }}>Organisation en 4 blocs de compétences (au lieu de 2 options)</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
              {slide.blocs.map((bloc, i) => (
                <div key={i} style={{ padding: '0.75rem 1rem', background: 'white', borderRadius: '10px', border: `2px solid ${bloc.color}`, display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ background: bloc.color, color: 'white', padding: '0.25rem 0.6rem', borderRadius: '6px', fontWeight: 'bold', fontSize: '0.8rem', flexShrink: 0, marginTop: '2px' }}>{bloc.num}</span>
                  <div>
                    <span style={{ fontSize: '0.9rem', color: theme.text, fontWeight: '600' }}>{bloc.title}</span>
                    <p style={{ fontSize: '0.82rem', color: '#64748B', marginTop: '0.2rem' }}>{bloc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {slide.content.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.6rem 0.8rem', background: '#F5F3FF', borderRadius: '8px', borderLeft: `3px solid ${theme.primary}` }}>
                  <CheckCircle size={16} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.88rem', color: theme.text }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'grid':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ padding: '1.2rem', background: '#F5F3FF', borderRadius: '12px', border: '1px solid #E9D5FF' }}>
                  <h3 style={{ fontSize: '1.1rem', color: theme.primary, marginBottom: '0.5rem' }}>{item.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#64748B' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'list':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ padding: '1rem', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <h4 style={{ color: theme.primary, marginBottom: '0.3rem' }}>{item.title}</h4>
                  <p style={{ fontSize: '0.9rem', color: '#64748B' }}>{item.desc}</p>
                </div>
              ))}
            </div>
            {slide.footer && (
              <p style={{ marginTop: '1.25rem', fontSize: '0.95rem', color: theme.text, fontStyle: 'italic', background: '#F5F3FF', padding: '1rem 1.25rem', borderRadius: '10px', borderLeft: `4px solid ${theme.primary}`, lineHeight: '1.6' }}>{slide.footer}</p>
            )}
          </div>
        );
      case 'blocks':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ padding: '1rem', background: 'white', borderRadius: '10px', border: `2px solid ${item.color}`, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ background: item.color, color: 'white', padding: '0.4rem 0.8rem', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.9rem' }}>{item.num}</span>
                  <span style={{ fontSize: '0.95rem', color: theme.text }}>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'table':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{slide.headers.map((h, i) => <th key={i} style={{ padding: '0.8rem', background: theme.primary, color: 'white', textAlign: 'left', fontSize: '0.9rem' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {slide.rows.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                      {row.map((cell, j) => <td key={j} style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #E2E8F0', fontSize: '0.9rem' }}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'summary':
        return (
          <div style={{ padding: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: theme.primary, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #FDF2F8 100%)', padding: '1.5rem', borderRadius: '12px' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', marginBottom: '0.8rem' }}>
                  <Target size={18} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  return renderContent();
};

const BACASSPPresentationGenerale = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slide = slides[currentSlide];

  return (
    <div style={{ background: theme.background, borderRadius: '16px', overflow: 'hidden' }}>
      <div style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`, padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'white', fontWeight: '600' }}>Présentation Générale BAC ASSP</span>
        <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>{currentSlide + 1} / {slides.length}</span>
      </div>
      <div style={{ padding: '1.5rem', minHeight: '400px' }}>
        <SlideRenderer slide={slide} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 1.5rem', borderTop: '1px solid #E9D5FF' }}>
        <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', borderRadius: '8px', border: 'none', background: currentSlide === 0 ? '#E2E8F0' : theme.primary, color: currentSlide === 0 ? '#94A3B8' : 'white', cursor: currentSlide === 0 ? 'default' : 'pointer' }}>
          <ChevronLeft size={18} /> Précédent
        </button>
        <button onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', borderRadius: '8px', border: 'none', background: currentSlide === slides.length - 1 ? '#E2E8F0' : theme.primary, color: currentSlide === slides.length - 1 ? '#94A3B8' : 'white', cursor: currentSlide === slides.length - 1 ? 'default' : 'pointer' }}>
          Suivant <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPPresentationGenerale;
